import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SummaryCard, SummaryCategory } from '../../types';
import { sampleSummaryCards } from '../../data/summaries/sampleCards';

interface SummariesState {
  cards: SummaryCard[];
  categories: SummaryCategory[];
  selectedCategory: string | null;
  searchQuery: string;
  sortBy: 'title' | 'subject' | 'difficulty' | 'createdAt' | 'viewCount' | 'likeCount';
  sortOrder: 'asc' | 'desc';
  viewMode: 'grid' | 'list';
  isLoading: boolean;
  error: string | null;
}

const initialState: SummariesState = {
  cards: sampleSummaryCards,
  categories: [
    {
      id: 'matematik',
      name: 'Matematik',
      description: 'Matematik konularına ait özet kartları',
      icon: 'Calculator',
      color: '#3B82F6',
      cardCount: sampleSummaryCards.filter(card => card.subject === 'matematik').length,
      isActive: true
    },
    {
        id: 'turkce',
        name: 'Türkçe',
        description: 'Türkçe konularına ait özet kartları',
        icon: 'BookOpen',
        color: '#EF4444',
        cardCount: sampleSummaryCards.filter(card => card.subject === 'turkce').length,
        isActive: true
      },
      {
        id: 'fen',
        name: 'Fen Bilimleri',
        description: 'Fen bilimleri konularına ait özet kartları',
        icon: 'Atom',
        color: '#10B981',
        cardCount: sampleSummaryCards.filter(card => card.subject === 'fen').length,
        isActive: true
      },
      {
        id: 'sosyal',
        name: 'Sosyal Bilgiler',
        description: 'Sosyal bilgiler konularına ait özet kartları',
        icon: 'Globe',
        color: '#F59E0B',
        cardCount: sampleSummaryCards.filter(card => card.subject === 'sosyal').length,
        isActive: true
      },
      {
        id: 'ingilizce',
        name: 'İngilizce',
        description: 'İngilizce konularına ait özet kartları',
        icon: 'Languages',
        color: '#8B5CF6',
        cardCount: sampleSummaryCards.filter(card => card.subject === 'ingilizce').length,
        isActive: true
      },
      {
        id: 'din',
        name: 'Din Kültürü',
        description: 'Din kültürü konularına ait özet kartları',
        icon: 'Heart',
        color: '#EC4899',
        cardCount: sampleSummaryCards.filter(card => card.subject === 'din').length,
        isActive: true
      }
  ],
  selectedCategory: null,
  searchQuery: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  viewMode: 'grid',
  isLoading: false,
  error: null
};

const summariesSlice = createSlice({
  name: 'summaries',
  initialState,
  reducers: {
    // Card Management (Admin only)
    setCards: (state, action: PayloadAction<SummaryCard[]>) => {
      state.cards = action.payload;
      // Update category card counts
      state.categories.forEach(category => {
        category.cardCount = state.cards.filter(card => 
          card.subject.toLowerCase() === category.id
        ).length;
      });
    },
    
    addCard: (state, action: PayloadAction<SummaryCard>) => {
      state.cards.push(action.payload);
      // Update category card count
      const category = state.categories.find(cat => 
        cat.id === action.payload.subject.toLowerCase()
      );
      if (category) {
        category.cardCount++;
      }
    },
    
    updateCard: (state, action: PayloadAction<SummaryCard>) => {
      const index = state.cards.findIndex(card => card.id === action.payload.id);
      if (index !== -1) {
        const oldSubject = state.cards[index].subject.toLowerCase();
        const newSubject = action.payload.subject.toLowerCase();
        
        state.cards[index] = action.payload;
        
        // Update category counts if subject changed
        if (oldSubject !== newSubject) {
          const oldCategory = state.categories.find(cat => cat.id === oldSubject);
          const newCategory = state.categories.find(cat => cat.id === newSubject);
          
          if (oldCategory) oldCategory.cardCount--;
          if (newCategory) newCategory.cardCount++;
        }
      }
    },
    
    deleteCard: (state, action: PayloadAction<string>) => {
      const cardIndex = state.cards.findIndex(card => card.id === action.payload);
      if (cardIndex !== -1) {
        const card = state.cards[cardIndex];
        const category = state.categories.find(cat => 
          cat.id === card.subject.toLowerCase()
        );
        if (category) {
          category.cardCount--;
        }
        state.cards.splice(cardIndex, 1);
      }
    },
    
    // User Interactions
    incrementViewCount: (state, action: PayloadAction<string>) => {
      const card = state.cards.find(card => card.id === action.payload);
      if (card) {
        card.viewCount++;
      }
    },
    
    toggleLike: (state, action: PayloadAction<string>) => {
      const card = state.cards.find(card => card.id === action.payload);
      if (card) {
        // This would normally check if user already liked it
        // For now, just increment
        card.likeCount++;
      }
    },
    
    // Filtering and Sorting
    setSelectedCategory: (state, action: PayloadAction<string | null>) => {
      state.selectedCategory = action.payload;
    },
    
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    
    setSortBy: (state, action: PayloadAction<SummariesState['sortBy']>) => {
      state.sortBy = action.payload;
    },
    
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    
    setViewMode: (state, action: PayloadAction<'grid' | 'list'>) => {
      state.viewMode = action.payload;
    },
    
    // Loading and Error States
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    }
  }
});

export const {
  setCards,
  addCard,
  updateCard,
  deleteCard,
  incrementViewCount,
  toggleLike,
  setSelectedCategory,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  setViewMode,
  setLoading,
  setError,
  clearError
} = summariesSlice.actions;

export default summariesSlice.reducer;